export class MenuInfo {
  /**
   * contains menu item information
   * @param {string|null} parent
   * @param {IMenuItem[]} items
   * @param {IMenuItem|null} active
   */
  constructor(parent, items, active) {
    this.parent = parent;
    this.items = items;
    this.active = active;
  }
}

/**
 * keeps track of the current menu etc
 */
export class MenuTracker {
  /**
   * Create a menu tracker object
   * @param {IMenu} menu
   */
  constructor(menu) {
    this._menu = menu;
    // filter menu: remove adult
    this._menu.items = this.removeAdult(this._menu.items);
    this._menu.items = this.removeEmptyFolders(this._menu.items);

    this._menuRoot = new MenuInfo(null, menu.items, this._findDefault(menu.items));
    this.stack = [this._menuRoot];
  }

  /**
   * Returns the active menu and item
   * @return {MenuInfo}
   */
  activeMenu() {
    const len = this.stack.length;
    return this.stack[len - 1];
  }

  /**
   * Activate the menu item in the current menu and returns the current menu information.
   * Acts as a NO-OP when the item is not found in the current list of items.
   * @param {IMenuItem} item - menu item
   * @returns {MenuInfo}
   */
  activate(item) {
    const last = this.stack[this.stack.length - 1];
    if (!item) {
      last.active = null;
    } else {
      const result = this._findOnTag(last.items, item.tag);
      if (result) {
        last.active = result;
      }
    }
    return last;
  }

  /**
   * Select the item in the MenuInfo items list
   * @param {MenuInfo} info
   * @param {IMenuItem} item
   * @return {MenuInfo}
   */
  selectItem(info, item) {
    const idx = this.stack.findIndex(x => x.parent === info.parent);
    if (idx > -1) {
      this.stack = this.stack.slice(0, idx + 1);
    }
    let notFound = true;
    while (this.stack.length > 0 && notFound) {
      const last = this.stack.pop();
      const found = this._findOnTag(last.items, item.tag);
      if (found) {
        last.active = found;
        this.stack.push(last);
        notFound = false;
        if (found.items && found.items.length > 0) {
          const defaultItem = this._findDefault(found.items);
          this.stack.push(new MenuInfo(item.tag, found.items, defaultItem));
        }
      }
    }
    return this.activeMenu();
  }

  /**
   * Find the parent menu of the current menu
   * @param {MenuInfo} currentMenu
   * @return {MenuInfo}
   */
  parentMenu(currentMenu) {
    const index = this.stack.findIndex(info => info.parent === currentMenu.parent);
    if (index > 0) {
      return this.stack[index - 1];
    }
    return null;
  }

  /**
   * Finds a menu item in items with the specified tag
   * @param {IMenuItem[]} items
   * @param {string} tag
   * @return {IMenuItem|null}
   * @private
   */
  _findOnTag(items, tag) {
    return items.find(item => item.tag === tag);
  }

  /**
   * Find the default menu item in the list of items
   * @param {IMenuItem[]} items
   * @returns {IMenuItem|null}
   * @private
   */
  _findDefault(items) {
    return items.find(item => item.isDefault);
  }

  get menuRoot() {
    return this._menuRoot;
  }

  resetTrackerToInitial() {
    this.stack = [this._menuRoot];
  }

  /**
   * filter out all adult pages
   * @param {IMenuItem[]} itemsArr
   * @return IMenuItem[]
   */
  removeAdult(itemsArr) {
    if (!itemsArr || itemsArr.length === 0) return undefined;
    let returnVal = itemsArr.map(item => {
      if (item.properties.includes('ADULT_REQUIRED')) return undefined;
      item.items = this.removeAdult(item.items);
      return item;
    });
    return returnVal.filter(el => el);
  }

  /**
   * filter out all empty folders
   * @param {IMenuItem[]} itemsArr
   * @return IMenuItem[]
   */
  removeEmptyFolders(itemsArr) {
    if (!itemsArr || itemsArr.length === 0) return undefined;
    let returnVal = itemsArr.map(item => {
      if (item.items && item.items.length === 0) return undefined;
      item.items = this.removeEmptyFolders(item.items);
      return item;
    });
    return returnVal.filter(el => el);
  }
}
